
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { MapPin, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface LocationAutocompleteProps {
  value: string;
  onChange: (location: string, coordinates?: [number, number], locationType?: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Enter location...",
  disabled = false
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const searchLocations = async (searchQuery: string) => {
    if (!searchQuery.trim() || disabled) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-mapbox-token');
      
      if (error) {
        console.error('Error getting Mapbox token:', error);
        return;
      }

      const mapboxToken = data.token;
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${mapboxToken}&types=country,region,place,locality&limit=5`
      );
      
      const result = await response.json();
      setSuggestions(result.features || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching locations:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    
    if (newValue.length > 2) {
      searchLocations(newValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: any) => {
    const locationName = suggestion.place_name;
    const coordinates: [number, number] = suggestion.center;
    const locationType = suggestion.place_type?.[0] || 'place';
    
    setQuery(locationName);
    onChange(locationName, coordinates, locationType);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const clearLocation = () => {
    setQuery('');
    onChange('');
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className="relative">
      <Label htmlFor="location">Location</Label>
      <div className="relative">
        <Input
          id="location"
          value={query}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`pr-20 ${disabled ? "bg-gray-100 text-gray-500" : ""}`}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {query && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={clearLocation}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <MapPin className="h-4 w-4 text-gray-400" />
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm">{suggestion.place_name}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
        </div>
      )}
    </div>
  );
};
